import { canReachBackendApi, resolveApiRequestUrl } from './apiRequestUrl';

/**
 * @returns {{ message: string, code?: string }}
 */
async function friendlyErrorFromResponse(res) {
  let body = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      body = await res.json();
    } catch {
      /* ignore parse errors */
    }
  }
  const serverMsg = typeof body?.error === 'string' ? body.error : null;
  switch (res.status) {
    case 401:
      return { message: 'Your session could not be verified. Please sign out and sign in again.' };
    case 400:
      return { message: serverMsg || 'The request was invalid. Please try again.' };
    case 402:
      if (serverMsg === 'INSUFFICIENT_CREDITS') {
        return {
          message: 'You do not have enough credits. Please buy a credit pack to continue.',
          code: 'INSUFFICIENT_CREDITS',
        };
      }
      return { message: serverMsg || 'Payment required.' };
    case 502:
      return { message: serverMsg || 'Image generation failed. Please try again.' };
    default:
      return { message: serverMsg || 'Something went wrong. Please try again.' };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {{
 *   blob: Blob;
 *   roomType: string;
 *   style: string;
 *   userNotes?: string;
 *   selectedRoom?: object;
 *   camera?: object;
 *   analysisJson?: object;
 *   decorationLevel?: string;
 *   colorPreferences?: object;
 *   imageQuality?: string;
 * }} params
 * @returns {Promise<
 *   | {
 *       ok: true;
 *       imageDataUrl: string;
 *       contentType: string;
 *       model: string;
 *       roomType: string;
 *       style: string;
 *       promptUsed: string;
 *       chargedCredits?: number;
 *       creditsAfter?: number;
 *       transactionId?: string;
 *     }
 *   | { ok: false; error: string; code?: string }
 * >}
 */
export async function requestFloorPlanRoomToRealisticImage(user, {
  blob,
  roomType,
  style,
  userNotes,
  projectId,
  selectedRoom,
  camera,
  analysisJson,
  decorationLevel,
  colorPreferences,
  imageQuality,
}) {
  if (!user) {
    return { ok: false, error: 'You must be signed in to generate a realistic room image.' };
  }
  if (!canReachBackendApi()) {
    return {
      ok: false,
      error: 'The app is not configured to reach the design server. Please contact support.',
    };
  }
  if (!blob || !(blob instanceof Blob)) {
    return { ok: false, error: 'Prepared image is missing. Upload and crop a floor plan area first.' };
  }
  if (typeof roomType !== 'string' || !roomType.trim()) {
    return { ok: false, error: 'Please select a room type.' };
  }
  if (typeof style !== 'string' || !style.trim()) {
    return { ok: false, error: 'Please select a design style.' };
  }

  try {
    const idToken = await user.getIdToken(true);
    const formData = new FormData();
    formData.append('image', blob, 'prepared-floor-plan-room.jpg');
    formData.append('roomType', roomType.trim());
    formData.append('style', style.trim());
    if (typeof userNotes === 'string' && userNotes.trim()) {
      formData.append('userNotes', userNotes.trim());
    }
    if (typeof projectId === 'string' && projectId.trim()) {
      formData.append('projectId', projectId.trim());
    }
    if (selectedRoom && typeof selectedRoom === 'object') {
      formData.append('selectedRoom', JSON.stringify(selectedRoom));
    }
    if (camera && typeof camera === 'object') {
      formData.append('camera', JSON.stringify(camera));
      if (typeof camera.aspectRatio === 'string' && camera.aspectRatio.trim()) {
        formData.append('aspectRatio', camera.aspectRatio.trim());
      }
      if (
        Number.isFinite(camera.outputWidth) &&
        Number.isFinite(camera.outputHeight) &&
        camera.outputWidth > 0 &&
        camera.outputHeight > 0
      ) {
        formData.append('outputSize', `${camera.outputWidth}x${camera.outputHeight}`);
      }
    }
    if (analysisJson && typeof analysisJson === 'object') {
      formData.append('analysisJson', JSON.stringify(analysisJson));
    }
    if (typeof decorationLevel === 'string' && decorationLevel.trim()) {
      formData.append('decorationLevel', decorationLevel.trim());
    }
    if (colorPreferences && typeof colorPreferences === 'object') {
      formData.append('colorPreferences', JSON.stringify(colorPreferences));
    }
    if (typeof imageQuality === 'string' && imageQuality.trim()) {
      formData.append('imageQuality', imageQuality.trim());
    }

    const res = await fetch(resolveApiRequestUrl('/api/test/floorplan-room-to-realistic-image'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: formData,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const imageBase64 = typeof body?.imageBase64 === 'string' ? body.imageBase64 : '';
      const contentType =
        typeof body?.contentType === 'string' ? body.contentType : 'image/jpeg';
      if (!imageBase64) {
        return { ok: false, error: 'The server did not return an image.' };
      }
      const project =
        body?.project && typeof body.project === 'object' ? body.project : undefined;
      return {
        ok: true,
        imageDataUrl: `data:${contentType};base64,${imageBase64}`,
        contentType,
        model: typeof body?.model === 'string' ? body.model : '',
        roomType: typeof body?.roomType === 'string' ? body.roomType : roomType.trim(),
        style: typeof body?.style === 'string' ? body.style : style.trim(),
        promptUsed: typeof body?.promptUsed === 'string' ? body.promptUsed : '',
        generatedImageUrl:
          typeof body?.generatedImageUrl === 'string' ? body.generatedImageUrl : '',
        chargedCredits:
          typeof body?.chargedCredits === 'number' ? body.chargedCredits : undefined,
        creditsAfter:
          typeof body?.creditsAfter === 'number' ? body.creditsAfter : undefined,
        transactionId:
          typeof body?.transactionId === 'string' ? body.transactionId : undefined,
        billingStatus:
          typeof body?.billingStatus === 'string' ? body.billingStatus : undefined,
        ...(project ? { project } : {}),
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? '');
    const likelyCors =
      typeof message === 'string' &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    return {
      ok: false,
      error:
        process.env.NODE_ENV === 'development' && message
          ? `Could not reach the server. (${message})`
          : likelyCors
            ? 'Could not reach the server. Check your connection and try again.'
            : 'Could not reach the server. Check your connection and try again.',
    };
  }
}
